import { formatAed } from '../finance/Money';
import { PromoCodeSummary, PromoHolderRow } from '../../types/promotions';

export { formatAed };

const DATE = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** Promo windows are Asia/Dubai (G-20); unparseable input is shown as-is. */
export function formatPromoDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (withTime ? DATE_TIME : DATE).format(d);
}

export function formatExpiry(iso: string | null): string {
  return iso ? formatPromoDate(iso) : 'No expiry';
}

/** "10% off (up to AED 50.00)" · "AED 25.00 off" */
export function discountText(p: Pick<PromoCodeSummary, 'discountType' | 'discountValue' | 'maxDiscountPerUse'>): string {
  if (p.discountType === 'PERCENT') {
    const pct = `${Number(p.discountValue)}% off`;
    return p.maxDiscountPerUse ? `${pct} (up to ${formatAed(p.maxDiscountPerUse)})` : pct;
  }
  return `${formatAed(p.discountValue)} off`;
}

export function slotsLeft(p: Pick<PromoCodeSummary, 'maxHolders' | 'holdersCount'>): number {
  return Math.max(0, p.maxHolders - p.holdersCount);
}

/** Flow C — sharing needs a usable code with a free slot (BR-24 / BR-26). */
export function shareBlockedReason(p: Pick<PromoCodeSummary, 'derivedStatus' | 'maxHolders' | 'holdersCount'>): string | null {
  if (p.derivedStatus === 'DISABLED' || p.derivedStatus === 'EXPIRED') return "This code can't be shared (disabled/expired).";
  if (slotsLeft(p) <= 0) return 'No slots left on this code.';
  return null;
}

/** Holder display name, or the pending contact with its state (AC-15 / AC-16). */
export function holderLabel(h: PromoHolderRow): string {
  if (h.customer) return h.customer.name || h.customer.phone || h.customer.email || h.customer.subjectId;
  if (h.pendingPhone) return `${h.pendingPhone} (not registered)`;
  if (h.pendingEmail) return `${h.pendingEmail} (not claimed)`;
  return '—';
}

/** Flow D step 2 source labels. */
export function sourceLabel(h: PromoHolderRow): string {
  if (h.source === 'ADMIN_SHARE') return h.sharedBy?.name ? `Shared by ${h.sharedBy.name}` : 'Shared by an admin';
  // AC-17: a self-claimer the admin then shared to keeps their claim source but carries a share date.
  if (h.sharedAt) return 'Claimed, then shared';
  if (h.source === 'LINK_CLAIM') return 'Self-claimed via link';
  return 'Self-claimed (typed)';
}
